import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrganizationService } from 'src/app/@shared/services/organization.service';
import {
  VersionAttributeConflictDetail,
  VersionAttributeConflictDetailApiItem,
  VersionAttributeFilterPair,
  mapVersionAttributeConflictDetail,
} from '../models/store-attribute.model';

interface ConflictDetailCacheEntry {
  detail$: Observable<VersionAttributeConflictDetail | null>;
  loading: boolean;
  error: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class VersionConflictDetailService {
  private cache = new Map<string, ConflictDetailCacheEntry>();

  constructor(
    private httpClient: HttpClient,
    private organizationService: OrganizationService,
  ) {}

  getConflictDetail(
    eventId: string,
    versionId: string,
    attributeFilterPairs: VersionAttributeFilterPair[],
  ): Observable<VersionAttributeConflictDetail | null> {
    const cacheKey = this.buildCacheKey(versionId, attributeFilterPairs);
    const existing = this.cache.get(cacheKey);
    if (existing) {
      return existing.detail$;
    }

    const baseUrl = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions`;
    const url = `${baseUrl}/GetVersionAttributeConflictDetail`;
    const body = {
      eventId,
      versionId,
      attributeFilters: attributeFilterPairs.map((pair) => ({
        attributeName: pair.attributeName,
        valueSourceField: pair.valueSourceField,
        value: pair.value,
      })),
    };

    const entry: ConflictDetailCacheEntry = {
      loading: true,
      error: false,
      detail$: of(null),
    };
    this.cache.set(cacheKey, entry);

    entry.detail$ = this.httpClient.post<VersionAttributeConflictDetailApiItem>(url, body).pipe(
      map((response) => mapVersionAttributeConflictDetail(response)),
      tap(() => {
        entry.loading = false;
      }),
      catchError(() => {
        entry.loading = false;
        entry.error = true;
        return of(null);
      }),
      shareReplay(1),
    );

    return entry.detail$;
  }

  isLoading(versionId: string, attributeFilterPairs: VersionAttributeFilterPair[]): boolean {
    return this.cache.get(this.buildCacheKey(versionId, attributeFilterPairs))?.loading ?? false;
  }

  hasError(versionId: string, attributeFilterPairs: VersionAttributeFilterPair[]): boolean {
    return this.cache.get(this.buildCacheKey(versionId, attributeFilterPairs))?.error ?? false;
  }

  clearCache(): void {
    this.cache.clear();
  }

  private buildCacheKey(versionId: string, attributeFilterPairs: VersionAttributeFilterPair[]): string {
    const normalized = [...attributeFilterPairs]
      .map((pair) => `${pair.attributeName}|${pair.valueSourceField}|${pair.value}`)
      .sort()
      .join(';');
    return `${versionId}::${normalized}`;
  }
}
